import { Download, FileText, Loader2, Stamp } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { formatCNPJ, formatDataHoraHistorico, formatDate } from '@/lib/utils/formatters';
import {
  MANIFESTACAO_SUSPENSA,
  useBaixarXmlNfse,
  useEventosNfse,
  useSelecionarNfsePdf,
  type NfseDocumento,
  type TipoManifestacaoNfse,
} from '@/services/ServicosNfse';
import { abrirEmNovaAba, baixarArquivo, pdfDoBase64 } from '../utils';
import { EventosNfseSection } from './EventosNfseSection';
import { ManifestacaoNfseDetalhe } from './ManifestacaoNfseDetalhe';
import { useDetalhesNfseModal } from './hooks';

interface DetalhesNfseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documento: NfseDocumento | null;
  /** A aba de onde a nota foi aberta. Só a tomada se manifesta (SPEC-011, §3, item 7). */
  tipo?: 'tomados' | 'prestados';
  /** Depois de uma manifestação registrada: a lista recarrega o aceite. */
  onManifestada?: () => void;
}

function formatValue(value: unknown) {
  if (value === null || value === undefined) return '-';
  const text = String(value).trim();
  return text ? text : '-';
}

function formatDocument(value: string | null | undefined) {
  const raw = value || '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 14) return formatCNPJ(digits);
  return formatValue(value);
}

/** `opSimpNac` do XML: 1 Nao Optante, 2 MEI, 3 ME/EPP. */
function descreverSimplesNacional(codigo: string | null | undefined) {
  if (!codigo) return codigo;

  const porCodigo: Record<string, string> = {
    '1': '1 - Não Optante',
    '2': '2 - Microempreendedor Individual (MEI)',
    '3': '3 - Microempresa ou Empresa de Pequeno Porte (ME/EPP)',
  };

  return porCodigo[codigo.trim()] ?? codigo;
}

/**
 * `tpRetISSQN` do XML: 1 Não Retido, 2 Retido pelo Tomador.
 *
 * Não confundir com o campo ISSRetido mostrado em "Serviço Prestado", que é outra coluna e
 * segue sendo lida como 0/1 — os dois convivem de propósito.
 */
function descreverRetencaoIssqn(codigo: string | null | undefined) {
  if (!codigo) return codigo;

  const porCodigo: Record<string, string> = {
    '1': '1 - Não Retido',
    '2': '2 - Retido pelo Tomador',
  };

  return porCodigo[codigo.trim()] ?? codigo;
}

function DetailField({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground font-medium">{label}</p>
      <p className="break-words text-sm">{formatValue(value)}</p>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <h3 className="text-1xl font-semibold tracking-tight">{title}</h3>
      <Separator />
      {children}
    </section>
  );
}

export function DetalhesNfseModal({
  open,
  onOpenChange,
  documento,
  tipo,
  onManifestada,
}: DetalhesNfseModalProps) {
  const u = useDetalhesNfseModal(documento, open);
  const { toast } = useToast();
  const baixarXml = useBaixarXmlNfse();
  const selecionarNfsePdf = useSelecionarNfsePdf();
  const [manifestacaoAberta, setManifestacaoAberta] = useState(false);
  /* As manifestações registradas por este detalhe, por chave. A linha que a página entrega é a de quando o detalhe
     abriu, e a lista recarregada só traz o aceite quando o banco o tiver: até lá, reabrir o painel ou o detalhe da
     mesma nota não oferece de novo Confirmar e Rejeitar (a trava contra a segunda manifestação). */
  const [desfechosRegistrados, setDesfechosRegistrados] = useState<
    Partial<Record<string, TipoManifestacaoNfse>>
  >({});

  const chaveAcessoDoc = u.chaveAcessoDoc;

  /* A nota com o evento 101101 (cancelamento) em MANFSEVE pode seguir com Status 4 e sem data de
     cancelamento (SPEC-011, decisão 13). Os eventos já vêm para a seção "Eventos da Nota"; aqui
     eles também dizem que a nota está cancelada, para o Status não mostrá-la como autorizada. */
  const { data: eventosDaNota } = useEventosNfse(chaveAcessoDoc, open);
  const canceladaPorEvento = (eventosDaNota ?? []).some(
    evento => evento.TpEvento?.trim() === '101101'
  );
  /* O botão Manifestar fica escondido enquanto a manifestação está suspensa (SPEC-011, decisão 10,
     03/11/2026): hoje ela não funciona porque o envio não passa pelo servidor. Volta, junto com o
     fluxo, pelo mesmo interruptor (manifestacao-suspensa.ts). Antes, o botão abria um painel que só
     explicava a indisponibilidade (T313). */
  const podeManifestar =
    !MANIFESTACAO_SUSPENSA && tipo === 'tomados' && !!documento && !!chaveAcessoDoc;

  const handleOpenChange = (aberto: boolean) => {
    if (!aberto) setManifestacaoAberta(false);
    onOpenChange(aberto);
  };

  const registrarDesfecho = (chave: string, desfecho: TipoManifestacaoNfse) => {
    setDesfechosRegistrados(atuais => ({ ...atuais, [chave]: desfecho }));
    onManifestada?.();
  };

  /* "Baixar XML" pela rota do módulo de NFS-e, que confere o vínculo do gestor e devolve o arquivo como anexo:
     baixa na mesma aba (SPEC-011, T6). O link temporário genérico assinava qualquer chave e abria uma aba. */
  const handleBaixarXml = async () => {
    if (!u.chaveAcessoDoc) return;

    try {
      const { arquivo, nome } = await baixarXml.mutateAsync({
        chaveAcessoDoc: u.chaveAcessoDoc,
        cnpjContribuinte: documento?.CNPJ_Contribuinte,
      });
      baixarArquivo(arquivo, nome);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao baixar XML',
        description:
          error instanceof Error ? error.message : 'Não foi possível baixar o XML desta NFS-e.',
      });
    }
  };

  const handleVisualizarDanfse = async () => {
    if (!u.chaveAcessoDoc) return;

    try {
      const response = await selecionarNfsePdf.mutateAsync(u.chaveAcessoDoc);
      const pdfBase64 = typeof response.data === 'string' ? response.data : '';

      if (response.status?.resultado === false || !pdfBase64) {
        throw new Error(response.status?.mensagem || 'DANFSe não retornado pela API.');
      }

      abrirEmNovaAba(pdfDoBase64(pdfBase64));
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao abrir o DANFSe',
        description:
          error instanceof Error ? error.message : 'Não foi possível abrir o DANFSe desta NFS-e.',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-full max-w-[calc(100%-2rem)] p-0 sm:max-w-2xl">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="text-2xl">Detalhes da NFS-e</DialogTitle>
          <DialogDescription className="space-y-1 text-base">
            <p className="text-sm">
              NFS-e n° {formatValue(u.data?.Numero || documento?.Numero)} /{' '}
              {formatValue(u.data?.Serie)}
            </p>
            <p className="break-all text-sm font-semibold">{formatValue(u.chaveAcessoDoc)}</p>
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[80vh] px-6 pb-6">
          {u.isLoading || u.isFetching ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Carregando detalhes da NFS-e...
            </div>
          ) : u.error ? (
            <div className="py-16 text-center text-destructive">{u.error.message}</div>
          ) : u.data ? (
            <div className="space-y-8">
              <DetailSection title="Prestador do Serviço">
                <div className="grid gap-6 md:grid-cols-2">
                  <DetailField label="Razão Social" value={u.data.RazaoSocialPrestador} />
                  <DetailField label="CNPJ" value={formatDocument(u.data.CPFCNPJPrestador)} />
                  <DetailField label="Cidade" value={u.data.CidadePrestador} />
                  <DetailField label="UF" value={u.data.UFPrestador} />
                  <DetailField label="Emitente" value={formatDocument(u.data.CNPJCPFEmitente)} />
                </div>
              </DetailSection>

              <DetailSection title="Tomador do Serviço">
                <div className="grid gap-6 md:grid-cols-2">
                  <DetailField label="Razão Social" value={u.data.RazaoSocialTomador} />
                  <DetailField label="CNPJ/CPF" value={formatDocument(u.data.CPFCNPJTomador)} />
                  <DetailField label="Cidade" value={u.data.CidadeTomador} />
                  <DetailField label="UF" value={u.data.UFTomador} />
                </div>
              </DetailSection>

              <DetailSection title="Serviço Prestado">
                <div className="space-y-6">
                  <DetailField label="Discriminação" value={u.data.Discriminacao} />
                  <div className="grid gap-6 md:grid-cols-3">
                    <DetailField label="Código Serviço" value={u.data.CodigoServico} />
                    <DetailField label="Alíquota Serviços" value={u.data.AliquotaServicos} />
                    <DetailField
                      label="ISS Retido"
                      value={
                        u.data.ISSRetido === '1'
                          ? 'Sim'
                          : u.data.ISSRetido === '0'
                            ? 'Não'
                            : u.data.ISSRetido
                      }
                    />
                    <DetailField label="Serviço Descrição" value={u.data.ServicoDescricao} />
                    <DetailField label="Origem" value={u.data.Origem} />
                    <DetailField label="nDFSe" value={u.data.nDFSe} />
                  </div>
                </div>
              </DetailSection>

              <DetailSection title="Valores">
                <div className="grid gap-6 md:grid-cols-3">
                  <DetailField label="Valor Serviços" value={u.data.ValorServicos} />
                  <DetailField label="Valor Deduções" value={u.data.ValorDeducoes} />
                  <DetailField label="Valor PIS" value={u.data.ValorPIS} />
                  <DetailField label="Valor COFINS" value={u.data.ValorCOFINS} />
                  <DetailField label="Valor INSS" value={u.data.ValorINSS} />
                  <DetailField label="Valor IR" value={u.data.ValorIR} />
                  <DetailField label="Valor CSLL" value={u.data.ValorCSLL} />
                  <DetailField label="Valor ISS" value={u.data.ValorISS} />
                  <DetailField label="Valor Crédito" value={u.data.ValorCredito} />
                </div>
              </DetailSection>

              <DetailSection title="IBS / CBS">
                <div className="grid gap-6 md:grid-cols-3">
                  <DetailField label="Base de Cálculo" value={u.data.vBCIBSCBS} />
                  <DetailField label="Aliq. Estadual" value={u.data.pIBSUF} />
                  <DetailField label="Aliq. Municipal" value={u.data.pIBSMun} />
                  <DetailField label="Aliq. CBS" value={u.data.pCBS} />
                  <DetailField label="Valor Estadual" value={u.data.vIBSUF} />
                  <DetailField label="Valor Municipal" value={u.data.vIBSMun} />
                  <DetailField label="Valor IBS" value={u.data.vIBS} />
                  <DetailField label="Valor CBS" value={u.data.vCBS} />
                </div>
              </DetailSection>

              <DetailSection title="Classificação Tributária (IBS / CBS)">
                <div className="grid gap-6 md:grid-cols-3">
                  <DetailField label="CST" value={u.data.CSTIBSCBS} />
                  <DetailField label="cClassTrib" value={u.data.cClassTribIBSCBS} />
                  <DetailField label="Indicador da Operação" value={u.data.cIndOp} />
                  <DetailField label="Código NBS" value={u.data.cNBS} />
                </div>
              </DetailSection>

              <DetailSection title="Localização">
                <div className="grid gap-6 md:grid-cols-3">
                  <DetailField label="Município do Emitente" value={u.data.xLocEmi} />
                  <DetailField label="Município da Prestação" value={u.data.xLocPrestacao} />
                  <DetailField label="Município de Incidência" value={u.data.xLocIncid} />
                </div>
              </DetailSection>

              <DetailSection title="ISS e Retenções">
                <div className="grid gap-6 md:grid-cols-3">
                  <DetailField label="Base de Cálculo do ISS" value={u.data.vBCISS} />
                  <DetailField label="Valor Total do Serviço" value={u.data.vServPrest} />
                  <DetailField label="Valor Líquido" value={u.data.vLiqNFSe} />
                  <DetailField
                    label="Regime do Simples"
                    value={descreverSimplesNacional(u.data.opSimpNac)}
                  />
                  <DetailField
                    label="Retenção do ISSQN"
                    value={descreverRetencaoIssqn(u.data.tpRetISSQN)}
                  />
                  <DetailField label="Retenção PIS/COFINS" value={u.data.tpRetPisCofins} />
                  <DetailField label="INSS Retido (vRetCP)" value={u.data.vRetCP} />
                  <DetailField label="IR Retido (vRetIRRF)" value={u.data.vRetIRRF} />
                  <DetailField label="CSLL Retida (vRetCSLL)" value={u.data.vRetCSLL} />
                </div>
              </DetailSection>

              <DetailSection title="Informações Gerais">
                <div className="grid gap-6 md:grid-cols-3">
                  <DetailField label="Série" value={u.data.Serie} />
                  <DetailField label="Número" value={u.data.Numero} />
                  <DetailField label="Tipo Operação" value={u.data.TipoOperacao} />
                  <DetailField label="Data Emissão" value={formatDate(u.data.DataEmissao)} />
                  <DetailField label="Hora Emissão" value={u.data.HoraEmissao} />
                  <DetailField label="Data da Autorização" value={formatDate(u.data.dhProcNFSe)} />
                  <DetailField
                    label="Status"
                    value={canceladaPorEvento ? 'Cancelada' : u.data.Status}
                  />
                  <DetailField
                    label="Data Cancelamento"
                    value={formatDate(u.data.DataCancelamento)}
                  />
                  <DetailField
                    label="Data Hora Gravação"
                    value={
                      u.data.DataHoraGravacao
                        ? formatDataHoraHistorico(u.data.DataHoraGravacao)
                        : null
                    }
                  />
                  <DetailField label="Data Exclusão" value={formatDate(u.data.DataExclusao)} />
                </div>
              </DetailSection>

              <DetailSection title="Eventos da Nota">
                <EventosNfseSection chaveAcessoDoc={u.chaveAcessoDoc} open={open} />
              </DetailSection>

              <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="min-w-[140px]"
                  onClick={handleBaixarXml}
                  disabled={baixarXml.isPending || !u.chaveAcessoDoc}
                >
                  {baixarXml.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Baixar XML
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="min-w-[140px]"
                  onClick={handleVisualizarDanfse}
                  disabled={selecionarNfsePdf.isPending || !u.chaveAcessoDoc}
                >
                  {selecionarNfsePdf.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                  Visualizar DANFSe
                </Button>

                {podeManifestar && (
                  <Button
                    type="button"
                    variant={manifestacaoAberta ? 'secondary' : 'outline'}
                    size="sm"
                    className="min-w-[140px]"
                    onClick={() => setManifestacaoAberta(aberta => !aberta)}
                    aria-expanded={manifestacaoAberta}
                  >
                    <Stamp className="h-4 w-4" />
                    Manifestar
                  </Button>
                )}
              </div>

              {podeManifestar && manifestacaoAberta && documento && chaveAcessoDoc && (
                <DetailSection title="Manifestação do Tomador">
                  <ManifestacaoNfseDetalhe
                    documento={documento}
                    chaveAcessoDoc={chaveAcessoDoc}
                    desfechoRegistrado={desfechosRegistrados[chaveAcessoDoc] ?? null}
                    onManifestada={desfecho => registrarDesfecho(chaveAcessoDoc, desfecho)}
                  />
                </DetailSection>
              )}
            </div>
          ) : (
            <div className="py-16 text-center text-muted-foreground">
              Nenhum detalhe disponível para esta NFS-e.
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
